// src/components/Navbar.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ROUTES } from "routers/paths";

const PANEL_MODE_KEY = "eme_panel_mode";
const PANEL_MODES = {
    APP: "app",
    WEB: "web",
};

export default function Navbar() {
    const navigate = useNavigate();
    const location = useLocation();

    const [usuario, setUsuario] = useState(() => {
        try {
            const raw = localStorage.getItem("usuario");
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });

    const [menuOpen, setMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [userMenuOpen, setUserMenuOpen] = useState(false);
    const [panelMode, setPanelMode] = useState(() => {
        const storedMode = localStorage.getItem(PANEL_MODE_KEY);
        return storedMode === PANEL_MODES.WEB ? PANEL_MODES.WEB : PANEL_MODES.APP;
    });

    // Sincroniza usuario si cambia en otra pestaña
    useEffect(() => {
        const onStorage = () => {
            try {
                const raw = localStorage.getItem("usuario");
                setUsuario(raw ? JSON.parse(raw) : null);
            } catch {
                setUsuario(null);
            }
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const handleLogout = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("usuario");
        navigate("/");
    };

    const nombreCompleto = usuario ? `${usuario.nombre} ${usuario.apellido}` : "—";

    const rolesUsuario = usuario?.roles || [];
    const sucursales = usuario?.sucursales || [];
    const comercios = usuario?.comercios || [];
    const esAdministrador = rolesUsuario.some((r) => (r.nombre || "").toLowerCase() === "administrador");
    const permisosSet = new Set(
        rolesUsuario.flatMap((rol) => {
            const rawPerms = Array.isArray(rol?.permisos)
                ? rol.permisos
                : Array.isArray(rol?.rol_permisos)
                    ? rol.rol_permisos
                    : [];
            return rawPerms
                .map((perm) => {
                    if (typeof perm === "string") return perm;
                    if (perm?.nombre) return perm.nombre;
                    if (perm?.permiso?.nombre) return perm.permiso.nombre;
                    return null;
                })
                .filter(Boolean)
                .map((perm) => String(perm).trim().toLowerCase());
        })
    );
    const canUsuariosApp = permisosSet.has("usuario.listar");
    const canAdministradores = permisosSet.has("administradores.listar");
    const canColaboradores = permisosSet.has("colaboradores.listar");
    const canEmeClubListar = permisosSet.has("emeclub.listar");
    const canEmeComerciosListar = permisosSet.has("emeclub.comercios.listar");
    const canEmeBeneficiosListar = permisosSet.has("emeclub.beneficios.listar");
    const canEmeBannersListar = permisosSet.has("emeclub.banners.listar");
    const canEmeInteresesListar = permisosSet.has("emeclub.intereses.listar");
    const canEmeNotificacionesListar = permisosSet.has("emeclub.notificaciones.listar");
    const canCanjesListar = permisosSet.has("canjes.listar");
    const canPoliconsultorioListar = permisosSet.has("policonsultorio.listar");
    const tieneSucursalPropia = Array.isArray(sucursales) && sucursales.length > 0;
    const tieneSucursalDeComercio =
        Array.isArray(comercios) &&
        comercios.some((c) => Array.isArray(c.sucursales) && c.sucursales.length > 0);
    const puedeVerEmeClub = tieneSucursalPropia || tieneSucursalDeComercio;
    const puedeVerEmeClubPorPermiso =
        canEmeComerciosListar ||
        canEmeBeneficiosListar ||
        canEmeBannersListar ||
        canEmeInteresesListar ||
        canEmeNotificacionesListar;
    const puedeVerEmeClubMenu = esAdministrador || ((canEmeClubListar || puedeVerEmeClubPorPermiso) && puedeVerEmeClub);
    const puedeVerPoliconsultorioMenu = canPoliconsultorioListar && tieneSucursalPropia;

    const visibleChildren = (children = []) => children.filter((child) => child.visible !== false);

    const appNavItems = [
        { label: "Dashboard", path: ROUTES.DASHBOARD, visible: true },

        // EME Club: si tiene sucursal propia o sucursal de comercio
        {
            label: "EME Club",
            path: "/eme-club",
            visible: puedeVerEmeClubMenu,
            children: [
                {
                    label: "Comercios",
                    path: ROUTES.EME_COMERCIOS,
                    visible: tieneSucursalPropia && (canEmeClubListar || canEmeComerciosListar),
                },
                {
                    label: "Beneficios",
                    path: ROUTES.EME_BENEFICIOS,
                    visible: tieneSucursalPropia && (canEmeClubListar || canEmeBeneficiosListar),
                },
                {
                    label: "Banners",
                    path: ROUTES.EME_BANNERS,
                    visible: tieneSucursalPropia && (canEmeClubListar || canEmeBannersListar),
                },
                {
                    label: "Intereses",
                    path: ROUTES.EME_INTERESES,
                    visible: tieneSucursalPropia && (canEmeClubListar || canEmeInteresesListar),
                },
                {
                    label: "Planes",
                    path: ROUTES.EME_PLANES,
                    visible: esAdministrador,
                },
                {
                    label: "Notificaciones",
                    path: ROUTES.EME_NOTIFICACIONES,
                    visible: tieneSucursalPropia && (canEmeClubListar || canEmeNotificacionesListar),
                },
                {
                    label: "Canjes",
                    path: ROUTES.EME_CANJES,
                    visible: tieneSucursalDeComercio && canCanjesListar,
                },
            ],
        },

        // Policonsultorio: solo si tiene sucursal asignada
        {
            label: "Policonsultorio",
            path: ROUTES.POLICONSULTORIO,
            visible: puedeVerPoliconsultorioMenu,
        },

        // Sucursales: solo si es administrador
        {
            label: "Sucursales",
            path: ROUTES.SUCURSALES,
            visible: esAdministrador,
        },

        // Personas: agrupa administradores, usuarios y colaboradores
        {
            label: "Personas",
            path: "/personas",
            visible: esAdministrador || canUsuariosApp || canAdministradores || canColaboradores,
            children: [
                { label: "Administradores", path: ROUTES.ADMINISTRADORES, visible: esAdministrador || canAdministradores },
                { label: "Colaboradores", path: ROUTES.COLABORADORES, visible: esAdministrador || canColaboradores },
                { label: "Usuarios app", path: ROUTES.USUARIOS, visible: esAdministrador || canUsuariosApp },
            ],
        },
    ];

    const webNavItems = [
        { label: "Dashboard", path: ROUTES.DASHBOARD, visible: true },
        {
            label: "EME club",
            path: "/sitio-web/eme-club",
            visible: true,
            children: [
                { label: "Banners", path: ROUTES.WEB_EME_BANNERS, visible: true },
                { label: "Datos institucionales", path: ROUTES.WEB_EME_DATOS_INSTIT, visible: true },
            ],
        },
    ];
    const navItems = panelMode === PANEL_MODES.WEB ? webNavItems : appNavItems;


    const isPathActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + "/");
    };

    const isNavItemActive = (item) => {
        const children = visibleChildren(item.children);
        if (children.length) {
            const childActive = children.some((child) => isPathActive(child.path));
            return childActive || (item.path ? isPathActive(item.path) : false);
        }
        return item.path ? isPathActive(item.path) : false;
    };

    const handleNavClick = (path) => {
        navigate(path);
        setMenuOpen(false); // cierra el menú en mobile
        setOpenDropdown(null);
    };

    const handlePanelModeChange = (nextMode) => {
        if (nextMode === panelMode) return;
        localStorage.setItem(PANEL_MODE_KEY, nextMode);
        window.dispatchEvent(new Event("panel-mode-change"));
        setPanelMode(nextMode);
        navigate(ROUTES.DASHBOARD);
        setMenuOpen(false);
        setOpenDropdown(null);
        setUserMenuOpen(false);
    };

    const toggleDropdown = (path) => {
        setOpenDropdown((current) => (current === path ? null : path));
    };

    useEffect(() => {
        setOpenDropdown(null);
        setUserMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        if (!menuOpen) {
            setOpenDropdown(null);
            setUserMenuOpen(false);
        }
    }, [menuOpen]);

    return (
        <nav className="bg-white border-b border-slate-100 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-16">
                    {/* Logo + título */}
                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            type="button"
                            onClick={() => handleNavClick("/dashboard")}
                            className="flex items-center gap-2"
                        >
                            <img
                                src="https://www.eme.com.ar/logo.webp"
                                alt="Logo EME"
                                className="h-10 w-32 object-contain"
                            />
                        </button>
                        <span className="hidden sm:inline text-xs font-semibold text-cyan-700 uppercase tracking-wide">
                            Panel de gestión
                        </span>
                    </div>

                    {/* Menú desktop */}
                    <div className="hidden md:flex items-center gap-3">
                        <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-1">
                            <button
                                type="button"
                                onClick={() => handlePanelModeChange(PANEL_MODES.APP)}
                                className={[
                                    "px-3 py-1 rounded-full text-xs font-semibold transition",
                                    panelMode === PANEL_MODES.APP
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-600 hover:text-slate-900",
                                ].join(" ")}
                            >
                                App móvil
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePanelModeChange(PANEL_MODES.WEB)}
                                className={[
                                    "px-3 py-1 rounded-full text-xs font-semibold transition",
                                    panelMode === PANEL_MODES.WEB
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-600 hover:text-slate-900",
                                ].join(" ")}
                            >
                                Sitio web
                            </button>
                        </div>

                        <ul className="flex items-center gap-3">
                            {navItems.filter((item) => item.visible).map((item) => {
                                const active = isNavItemActive(item);
                                const children = visibleChildren(item.children);
                                const hasChildren = children.length > 0;

                                if (!hasChildren) {
                                    return (
                                        <li key={item.path}>
                                            <button
                                                type="button"
                                                onClick={() => handleNavClick(item.path)}
                                                className={[
                                                    "px-3 py-1.5 rounded-full text-xs sm:text-sm transition whitespace-nowrap",
                                                    active
                                                        ? "bg-slate-100 text-slate-900 font-semibold"
                                                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                                                ].join(" ")}
                                            >
                                                {item.label}
                                            </button>
                                        </li>
                                    );
                                }

                                const dropdownOpen = openDropdown === item.path;

                                return (
                                    <li
                                        key={item.path}
                                        className="relative"
                                        onMouseEnter={() => setOpenDropdown(item.path)}
                                        onMouseLeave={() => setOpenDropdown(null)}
                                    >
                                        <button
                                            type="button"
                                            onClick={() => toggleDropdown(item.path)}
                                            className={[
                                                "px-3 py-1.5 rounded-full text-xs sm:text-sm transition whitespace-nowrap flex items-center gap-1.5",
                                                active
                                                    ? "bg-slate-100 text-slate-900 font-semibold"
                                                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                                            ].join(" ")}
                                        >
                                            {item.label}
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                className={`size-3 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                                            >
                                                <path
                                                    d="M6 9l6 6 6-6"
                                                    stroke="currentColor"
                                                    strokeWidth="1.8"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </button>

                                        {dropdownOpen && children.length > 0 && (
                                            <div className="absolute right-0 top-full z-20 w-48 rounded-lg border border-slate-100 bg-white shadow-lg py-1">
                                                {children.map((child) => {
                                                    const childActive = isPathActive(child.path);
                                                    return (
                                                        <button
                                                            key={child.path}
                                                            type="button"
                                                            onClick={() => handleNavClick(child.path)}
                                                            className={[
                                                                "block w-full text-left px-3 py-2 text-sm",
                                                                childActive
                                                                    ? "bg-slate-50 text-slate-900 font-semibold"
                                                                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                                                            ].join(" ")}
                                                        >
                                                            {child.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>

                        {/* Usuario + logout */}
                        <div className="relative flex items-center gap-3">
                            <button
                                type="button"
                                onClick={() => setUserMenuOpen((v) => !v)}
                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-800 shadow-sm hover:bg-slate-50"
                            >
                                <span className="mr-1 max-w-[120px] sm:max-w-none truncate">{nombreCompleto}</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className={`size-4 transition-transform ${userMenuOpen ? "rotate-180" : ""}`}
                                >
                                    <path
                                        d="M6 9l6 6 6-6"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </button>
                            {userMenuOpen && (
                                <div className="absolute right-0 top-full mt-1 w-48 rounded-lg border border-slate-100 bg-white shadow-lg py-1 z-30">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            navigate(ROUTES.CONFIGURACION);
                                            setUserMenuOpen(false);
                                        }}
                                        className="block w-full text-left px-3 py-2 text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                                    >
                                        Configuración
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setUserMenuOpen(false);
                                            handleLogout();
                                        }}
                                        className="block w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                                    >
                                        Cerrar sesión
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Mobile: usuario + botón menú */}
                    <div className="flex md:hidden items-center gap-2">
                        <span className="text-xs font-semibold text-slate-800 max-w-[120px] truncate">
                            {nombreCompleto}
                        </span>
                        <button
                            type="button"
                            onClick={() => setMenuOpen((v) => !v)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-slate-700 hover:text-slate-900 hover:bg-slate-100 focus:outline-none"
                            aria-label="Abrir menú"
                        >
                            {menuOpen ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className="size-5"
                                >
                                    <path
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        d="M6 6l12 12M18 6 6 18"
                                    />
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    className="size-5"
                                >
                                    <path
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        d="M4 7h16M4 12h16M4 17h16"
                                    />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {menuOpen && (
                <div className="md:hidden border-t border-slate-100 bg-white shadow-sm">
                    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 space-y-2">
                        <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 p-1">
                            <button
                                type="button"
                                onClick={() => handlePanelModeChange(PANEL_MODES.APP)}
                                className={[
                                    "px-3 py-1 rounded-full text-xs font-semibold transition",
                                    panelMode === PANEL_MODES.APP
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-600 hover:text-slate-900",
                                ].join(" ")}
                            >
                                App móvil
                            </button>
                            <button
                                type="button"
                                onClick={() => handlePanelModeChange(PANEL_MODES.WEB)}
                                className={[
                                    "px-3 py-1 rounded-full text-xs font-semibold transition",
                                    panelMode === PANEL_MODES.WEB
                                        ? "bg-white text-slate-900 shadow-sm"
                                        : "text-slate-600 hover:text-slate-900",
                                ].join(" ")}
                            >
                                Sitio web
                            </button>
                        </div>

                        <ul className="flex flex-col gap-1">
                            {navItems
                                .filter((item) => item.visible)
                                .map((item) => {
                                    const active = isNavItemActive(item);
                                    const children = visibleChildren(item.children);
                                    const hasChildren = children.length;

                                    if (!hasChildren) {
                                        return (
                                            <li key={item.path}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleNavClick(item.path)}
                                                    className={[
                                                        "w-full text-left px-3 py-2 rounded-lg text-sm transition",
                                                        active
                                                            ? "bg-slate-100 text-slate-900 font-semibold"
                                                            : "text-slate-700 hover:bg-slate-50 hover:text-slate-900",
                                                    ].join(" ")}
                                                >
                                                    {item.label}
                                                </button>
                                            </li>
                                        );
                                    }

                                    const dropdownOpen = openDropdown === item.path;

                                    return (
                                        <li key={item.path}>
                                            <button
                                                type="button"
                                                onClick={() => toggleDropdown(item.path)}
                                                className={[
                                                    "w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition",
                                                    active
                                                        ? "bg-slate-100 text-slate-900 font-semibold"
                                                        : "text-slate-700 hover:bg-slate-50 hover:text-slate-900",
                                                ].join(" ")}
                                            >
                                                <span>{item.label}</span>
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    className={`size-4 transition-transform ${dropdownOpen ? "rotate-180" : ""}`}
                                                >
                                                    <path
                                                        d="M6 9l6 6 6-6"
                                                        stroke="currentColor"
                                                        strokeWidth="1.8"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </button>
                                            {dropdownOpen && children.length > 0 && (
                                                <ul className="mt-1 ml-3 space-y-1 border-l border-slate-100 pl-3">
                                                    {children.map((child) => {
                                                        const childActive = isPathActive(child.path);
                                                        return (
                                                            <li key={child.path}>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleNavClick(child.path)}
                                                                    className={[
                                                                        "w-full text-left px-3 py-2 rounded-md text-sm",
                                                                        childActive
                                                                            ? "bg-slate-100 text-slate-900 font-semibold"
                                                                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                                                                    ].join(" ")}
                                                                >
                                                                    {child.label}
                                                                </button>
                                                            </li>
                                                        );
                                                    })}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                })}
                        </ul>


                        <div className="pt-2 mt-2 border-t border-slate-100 flex items-center justify-between">
                            <span className="text-xs text-slate-500">
                                Sesión: <span className="font-semibold text-slate-800">{nombreCompleto}</span>
                            </span>
                            <button
                                type="button"
                                onClick={handleLogout}
                                className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700"
                            >
                                <span>Cerrar sesión</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                    className="size-4"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M7.5 3.75A1.5 1.5 0 0 0 6 5.25v13.5a1.5 1.5 0 0 0 1.5 1.5h6a1.5 1.5 0 0 0 1.5-1.5V15a.75.75 0 0 1 1.5 0v3.75a3 3 0 0 1-3 3h-6a3 3 0 0 1-3-3V5.25a3 3 0 0 1 3-3h6a3 3 0 0 1 3 3V9A.75.75 0 0 1 15 9V5.25a1.5 1.5 0 0 0-1.5-1.5h-6Zm10.72 4.72a.75.75 0 0 1 1.06 0l3 3a.75.75 0 0 1 0 1.06l-3 3a.75.75 0 1 1-1.06-1.06l1.72-1.72H9a.75.75 0 0 1 0-1.5h10.94l-1.72-1.72a.75.75 0 0 1 0-1.06Z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </nav>
    );
}
